import { signal } from "@preact/signals";
import * as preact from "preact";

// App-wide modal controller used by ModalHostView.

export interface ModalOptions {
  clickToCancel?: boolean;
  closeButton?: boolean;
  center?: boolean;
  minHeight?: number;
  addToBody?: boolean;
  width?: number;
  title?: string;
  /** Fills the screen; by default dialogs do on phones (ModalHostView) */
  fullScreen?: boolean;
  /** A small dialog (an alert or confirm) staying centred on phones */
  compact?: boolean;
  wide?: boolean;
  className?: string;
  undoZoom?: boolean;
  /** Returns true to continue cancelling, false to not cancel */
  confirmCancelCallback?: () => boolean;
  cancelCallback?: () => void;
  onShow?: () => void;
  view?: {
    remove?: () => void;
    setFocus?: () => void;
  };
}

export interface ModalHost {
  cancel(event?: any, skipCallback?: boolean): boolean;
  cancelAll(): boolean;
  isHostConnected(): boolean;
  isShown(): boolean;
  show(component: preact.ComponentChild, options?: ModalOptions): void;
  showLegacy(element: HTMLElement, options?: ModalOptions): void;
}

/**
 * Whether a full-screen modal is open, which the iOS app dims its header and
 * hides its tab bar behind (nativeHeader.ts)
 */
export const fullScreenShown = signal(false);

let host: ModalHost | undefined;
let requestSpace: ((height: number) => void) | undefined;

const connectedHost = () => {
  if (!host) {
    return undefined;
  }

  // Detached test/bookmarklet roots can leave behind a registered host. Treat
  // those as unavailable so callers can fail clearly instead of rendering into
  // a stale host.
  if (!host.isHostConnected()) {
    host = undefined;
    return undefined;
  }

  return host;
};

export const registerHost = (nextHost: ModalHost) => {
  host = nextHost;
};

export const unregisterHost = (oldHost: ModalHost) => {
  if (host === oldHost) {
    host = undefined;
  }
};

export const show = (
  component: preact.ComponentChild,
  options?: ModalOptions
) => {
  const nextHost = connectedHost();
  if (!nextHost) {
    return false;
  }

  nextHost.show(component, options);
  return true;
};

export const showLegacy = (element: HTMLElement, options?: ModalOptions) => {
  const nextHost = connectedHost();
  if (!nextHost) {
    return false;
  }

  nextHost.showLegacy(element, options);
  return true;
};

export const cancel = (event?: any, skipCallback?: boolean) => {
  return connectedHost()?.cancel(event, skipCallback) ?? false;
};

export const cancelAll = () => {
  return connectedHost()?.cancelAll() ?? false;
};

export const isShown = () => connectedHost()?.isShown() ?? false;

export const setRequestSpace = (
  nextRequestSpace: ((height: number) => void) | undefined
) => {
  requestSpace = nextRequestSpace;
};

export const requestModalSpace = (height: number) => {
  requestSpace?.(height);
};
